import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  MessageSquare,
  Lock,
  Tags,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/request/StatusBadge";
import { PodBadge } from "@/components/request/PodBadge";
import { PriorityBadge } from "@/components/request/PriorityBadge";
import { TypeBadge } from "@/components/request/TypeBadge";
import { MessageThread } from "@/components/request/MessageThread";
import { FileAttachment } from "@/components/request/FileAttachment";
import { RequestTimeline } from "@/components/request/RequestTimeline";
import { RequestWatchersIndicator } from "@/components/request/RequestWatchersIndicator";
import { ChangeClassificationDialog } from "@/components/request/ChangeClassificationDialog";
import { JiraTicketsCard } from "@/components/request/JiraTicketsCard";
import { AssignDialog } from "@/components/request/AssignDialog";
import {
  useRequest,
  useApproveRequest,
  useRejectRequest,
  useUpdateStatus,
  useSendClarification,
  useRequestTimeline,
  useRequestTickets,
  requestKeys,
} from "@/hooks/useRequests";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/use-toast";
import { workflowApi, usersApi } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import {
  ALLOWED_TRANSITIONS,
  STATUS_LABELS,
  REGION_LABELS,
} from "@/lib/constants";
import type { RequestStatus, MentionDetail } from "@/lib/types";

interface UserSuggestion {
  oid: string;
  email: string;
  display_name: string;
}

const REJECTION_REASONS = [
  "Out of scope",
  "Duplicate request",
  "Insufficient information",
  "Not prioritised this cycle",
  "Alternative solution exists",
  "Other",
];

export function ReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { isPM, isReviewer, user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [newStatus, setNewStatus] = useState<RequestStatus | "">("");
  const [rejectReason, setRejectReason] = useState("");
  const [classificationOpen, setClassificationOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  // Features: create the ND ticket on approval? ("none" = no ticket)
  const [featureTicket, setFeatureTicket] = useState<"none" | "ND">("none");
  // Defects: which project to raise a ticket in on approval ("none" = no ticket).
  const [approveProject, setApproveProject] = useState("none");
  const [rejectComment, setRejectComment] = useState("");
  const [clarifyText, setClarifyText] = useState("");
  const [isClaiming, setIsClaiming] = useState(false);
  const [clarifyMentions, setClarifyMentions] = useState<MentionDetail[]>([]);
  const [clarifySuggestions, setClarifySuggestions] = useState<
    UserSuggestion[]
  >([]);
  const [showClarifySuggestions, setShowClarifySuggestions] = useState(false);
  const clarifyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: req, isLoading, isError } = useRequest(id ?? "");
  const { data: timeline } = useRequestTimeline(id ?? "");

  const approve = useApproveRequest(id ?? "");
  // Projects a defect's ticket can go to on approval (reviewers only).
  const { data: ticketInfo } = useRequestTickets(id ?? "", isReviewer);
  const reject = useRejectRequest(id ?? "");
  const updateStatus = useUpdateStatus(id ?? "");

  const isRejecting = newStatus === "Rejected";

  const clarify = useSendClarification(id ?? "");

  // IMPROVEMENT: Sync status dropdown with current request status
  // When request status changes, update the "Update Status" dropdown to show current status
  useEffect(() => {
    if (req) {
      setNewStatus("");
      setRejectReason("");
      setRejectComment("");
    }
  }, [req?.status]);

  if (!id) return <Navigate to="/dashboard" replace />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isError || !req) return <Navigate to="/dashboard" replace />;

  const isFinalStatus = req.status === "Cancelled" || req.status === "Rejected";
  const isApproved = req.status === "Approved";
  // Type and pod decide the Jira project, so they're fixed once approved.
  const canChangeClassification =
    isReviewer &&
    !req.jira_ticket_key &&
    ["Submitted", "InReview", "AwaitingInfo", "InfoReceived"].includes(
      req.status,
    );

  // Approving goes through the Approve panel, where the reviewer chooses the
  // Jira board; approving from here used to create a BDS ticket regardless.
  const statusOptions = ALLOWED_TRANSITIONS[req.status].filter(
    (s) => s !== "Approved",
  );
  const canApproveFromHere =
    ALLOWED_TRANSITIONS[req.status].includes("Approved");

  const handleApprove = () => {
    const isDefect = req.request_type === "Defect";
    // A ticket raised on demand already covers this request.
    const ticketProject =
      isDefect && !req.jira_ticket_key && approveProject !== "none"
        ? approveProject
        : undefined;
    const createTicket =
      !isDefect && featureTicket === "ND" && !req.jira_ticket_key;
    approve.mutate(
      { create_jira_ticket: createTicket, ticket_project: ticketProject },
      {
        onSuccess: (result) => {
          if (result.ticket_error) {
            toast({
              title: "Approved, but the Jira ticket wasn't created",
              description: `${result.ticket_error} You can raise it from Jira Tickets.`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Request approved",
              description: result.ticket_key
                ? `${result.ticket_key} created.`
                : createTicket
                  ? "Creating Jira ticket…"
                  : "No Jira ticket was created.",
            });
          }
          setApproveProject("none");
          setFeatureTicket("none");
        },
        onError: (err) =>
          toast({
            title: "Failed to approve",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleStatusChange = () => {
    if (!newStatus) return;
    const onSuccess = () => {
      toast({ title: `Status updated to ${STATUS_LABELS[newStatus]}` });
      setNewStatus("");
      setRejectReason("");
      setRejectComment("");
    };
    const onError = (err: Error) =>
      toast({
        title: "Failed to update status",
        description: err.message,
        variant: "destructive",
      });

    if (isRejecting) {
      reject.mutate(
        { reason: rejectReason, comment: rejectComment || undefined },
        { onSuccess, onError },
      );
    } else {
      updateStatus.mutate({ status: newStatus }, { onSuccess, onError });
    }
  };

  const getClarifyMentionContext = (text: string, cursorPos: number) => {
    const beforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf("@");
    if (lastAtIndex === -1) return null;
    const afterAt = beforeCursor.substring(lastAtIndex + 1);
    if (afterAt.includes(" ")) return null;
    return { query: afterAt, atIndex: lastAtIndex };
  };

  const handleClarifyTextChange = async (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    setClarifyText(newText);
    const context = getClarifyMentionContext(newText, cursorPos);
    if (context && context.query.length > 0) {
      setShowClarifySuggestions(true);
      try {
        const users = await usersApi.list(context.query);
        setClarifySuggestions(
          users.filter((u) => !clarifyMentions.some((m) => m.oid === u.oid)),
        );
      } catch {
        setClarifySuggestions([]);
      }
    } else {
      setShowClarifySuggestions(false);
      setClarifySuggestions([]);
    }
  };

  const handleClarifyMentionSelect = (u: UserSuggestion) => {
    const cursorPos = clarifyTextareaRef.current?.selectionStart || 0;
    const beforeCursor = clarifyText.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf("@");
    if (lastAtIndex === -1) return;
    const newText = `${clarifyText.substring(0, lastAtIndex)}@${u.display_name} ${clarifyText.substring(cursorPos)}`;
    setClarifyText(newText);
    setClarifyMentions([
      ...clarifyMentions,
      { oid: u.oid, name: u.display_name, email: u.email },
    ]);
    setShowClarifySuggestions(false);
    setClarifySuggestions([]);
  };

  const handleClarify = () => {
    if (!clarifyText.trim()) return;
    clarify.mutate(
      {
        body: clarifyText.trim(),
        mentions: clarifyMentions.map((m) => m.oid),
      },
      {
        onSuccess: () => {
          toast({
            title: "Clarification sent",
            description: "Requestor has been notified by email.",
          });
          setClarifyText("");
          setClarifyMentions([]);
        },
        onError: (err) =>
          toast({
            title: "Failed to send",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleClaim = async () => {
    setIsClaiming(true);
    try {
      await workflowApi.claimRequest(id ?? "");
      toast({
        title: "Request claimed",
        description: "You are now working on this request.",
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(id ?? ""),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Failed to claim",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const handleUnclaim = async () => {
    setIsClaiming(true);
    try {
      await workflowApi.unclaimRequest(id ?? "");
      toast({
        title: "Claim released",
        description: "Request is now available for others.",
      });
      void queryClient.invalidateQueries({
        queryKey: requestKeys.detail(id ?? ""),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Failed to release",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div className="space-y-6 relative min-h-full">
      {/* Watermark for cancelled/rejected requests */}
      {(req.status === "Cancelled" || req.status === "Rejected") && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
          <div
            className={`text-9xl font-bold opacity-10 rotate-[-45deg] select-none whitespace-nowrap ${
              req.status === "Cancelled" ? "text-orange-600" : "text-red-600"
            }`}
          >
            {req.status.toUpperCase()}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/dashboard">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Dashboard
          </Link>
        </Button>
      </div>

      <div className="flex items-start gap-3">
        <div className="space-y-1 min-w-0 flex-1">
          <h1 className="text-2xl font-bold">{req.title}</h1>
          {req.reference_id && (
            <p className="font-mono text-sm text-muted-foreground">
              {req.reference_id}
            </p>
          )}
        </div>
        <div className="shrink-0 mt-1">
          <StatusBadge status={req.status} className="text-sm px-3 py-1" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <TypeBadge type={req.request_type} />
        <PodBadge pod={req.pod} />
        <PriorityBadge priority={req.priority} />
        <div className="ml-auto">
          <RequestWatchersIndicator requestId={req.id} showNames={isReviewer} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Business Problem" value={req.business_problem} />
              <Field
                label="Region"
                value={req.region.map((r) => REGION_LABELS[r] ?? r).join(", ")}
              />
              {req.expected_outcome && (
                <Field label="Expected Outcome" value={req.expected_outcome} />
              )}
              {req.steps_to_reproduce && (
                <Field
                  label="Steps to Reproduce"
                  value={req.steps_to_reproduce}
                />
              )}
              <Field label="Affected Area" value={req.affected_area} />
              {req.additional_context && (
                <Field
                  label="Additional Context"
                  value={req.additional_context}
                />
              )}
              <div className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
                <span>
                  Submitted by {req.submitter_name} ({req.submitter_email})
                </span>
                <span>{formatDateTime(req.created_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Thread */}
          <Card
            className={isFinalStatus ? "opacity-60 pointer-events-none" : ""}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Conversation</CardTitle>
                {isFinalStatus && (
                  <span className="text-xs text-red-600 font-semibold">
                    Read-only
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isFinalStatus && (
                <p className="text-sm text-muted-foreground mb-4 p-3 bg-muted rounded-md">
                  This request is {req.status.toLowerCase()}. Conversation is
                  read-only — no new messages can be added.
                </p>
              )}
              <MessageThread
                requestId={req.id}
                internalOnly
                disabled={isFinalStatus}
              />
            </CardContent>
          </Card>

          {/* Timeline */}
          {timeline && timeline.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Request Lifecycle</CardTitle>
              </CardHeader>
              <CardContent>
                <RequestTimeline events={timeline} />
              </CardContent>
            </Card>
          )}

          {/* Files */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Attachments</CardTitle>
            </CardHeader>
            <CardContent>
              <FileAttachment requestId={req.id} canUpload={!isFinalStatus} />
            </CardContent>
          </Card>
        </div>

        {/* Actions sidebar */}
        {isReviewer && (
          <div className="space-y-4">
            {/* Status: Final - No actions allowed */}
            {isFinalStatus && (
              <Card className="border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                <CardHeader>
                  <CardTitle className="text-base text-gray-700 dark:text-gray-300">
                    Request Finalized
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {req.status === "Rejected"
                      ? "This request has been rejected. No further actions are available."
                      : "This request has been cancelled. No further actions are available."}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Approve */}
            {![
              "Approved",
              "Rejected",
              "Completed",
              "Closed",
              "Cancelled",
              "InProgress",
            ].includes(req.status) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base text-green-700">
                    Approve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {req.jira_ticket_key ? (
                    <p className="text-sm text-muted-foreground mb-3">
                      {req.jira_ticket_key} already exists, so approving won't
                      create another ticket.
                    </p>
                  ) : req.request_type === "Defect" ? (
                    <div className="mb-3 space-y-1.5">
                      <label
                        htmlFor="approve-ticket-project"
                        className="text-sm text-muted-foreground"
                      >
                        Jira ticket on approval
                      </label>
                      <Select
                        value={approveProject}
                        onValueChange={setApproveProject}
                      >
                        <SelectTrigger id="approve-ticket-project">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            Don't create a ticket
                          </SelectItem>
                          {(ticketInfo?.projects ?? []).map((p) => (
                            <SelectItem key={p.key} value={p.key}>
                              {p.key} — {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {approveProject === "none" && (
                        <p className="text-xs text-muted-foreground">
                          You can raise one later from Jira Tickets.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="mb-3 space-y-1.5">
                      <label
                        htmlFor="approve-feature-ticket"
                        className="text-sm text-muted-foreground"
                      >
                        Jira ticket on approval
                      </label>
                      <Select
                        value={featureTicket}
                        onValueChange={(v) =>
                          setFeatureTicket(v as "none" | "ND")
                        }
                      >
                        <SelectTrigger id="approve-feature-ticket">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            Don&apos;t create a ticket
                          </SelectItem>
                          <SelectItem value="ND">
                            ND — Network Development
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 gap-2"
                    onClick={handleApprove}
                    disabled={approve.isPending || isFinalStatus || isApproved}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {approve.isPending ? "Approving…" : "Approve Request"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Reviewers: every ticket (raise for defects, link for all) */}
            {isReviewer && <JiraTicketsCard request={req} />}

            {/* Jira ticket highlight (requesters) */}
            {req.jira_ticket_key && !isReviewer ? (
              <Card className="border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-blue-800 dark:text-blue-300">
                    Jira Ticket Created
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <a
                    href={req.jira_ticket_url ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full rounded-lg border border-blue-300 dark:border-blue-800 bg-white dark:bg-gray-900 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors group"
                  >
                    <span className="text-lg font-bold text-blue-700 tracking-tight">
                      {req.jira_ticket_key}
                    </span>
                    <ExternalLink className="h-4 w-4 text-blue-500 group-hover:text-blue-700" />
                  </a>
                  {req.cr_ticket_key && (
                    <a
                      href={req.cr_ticket_url ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between w-full rounded-lg border border-violet-300 dark:border-violet-800 bg-white dark:bg-gray-900 px-4 py-2 hover:bg-violet-50 dark:hover:bg-violet-950 transition-colors group"
                    >
                      <span className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                        Change Request {req.cr_ticket_key}
                      </span>
                      <ExternalLink className="h-4 w-4 text-violet-500 group-hover:text-violet-700" />
                    </a>
                  )}
                  <p className="text-xs text-blue-700">
                    Implementation ticket — click to open in Jira.
                  </p>
                </CardContent>
              </Card>
            ) : req.status === "Approved" &&
              !req.jira_ticket_key &&
              req.jira_ticket_pending ? (
              <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                <CardContent className="flex items-center gap-3 py-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent flex-shrink-0" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Creating Jira ticket…
                  </p>
                </CardContent>
              </Card>
            ) : null}

            {/* Claim status */}
            {(req.claimed_by_oid || req.claimed_by_email) && (
              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-amber-900 dark:text-amber-300">
                    Claimed By
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    {req.claimed_by_email}
                  </p>
                </CardContent>
              </Card>
            )}

            {isReviewer &&
              !isFinalStatus &&
              (req.claimed_by_email === user?.email ||
              req.claimed_by_oid === user?.oid ? (
                <Button
                  className="w-full gap-2"
                  onClick={handleUnclaim}
                  disabled={isClaiming}
                  variant="outline"
                >
                  <Lock className="h-4 w-4" />
                  {isClaiming ? "Releasing…" : "Unclaim This Request"}
                </Button>
              ) : !(req.claimed_by_oid || req.claimed_by_email) ? (
                <Button
                  className="w-full gap-2"
                  onClick={handleClaim}
                  disabled={isClaiming}
                  variant="outline"
                >
                  <Lock className="h-4 w-4" />
                  {isClaiming ? "Claiming…" : "Claim This Request"}
                </Button>
              ) : (
                <Button
                  className="w-full gap-2"
                  onClick={handleClaim}
                  disabled={isClaiming}
                  variant="outline"
                >
                  <Lock className="h-4 w-4" />
                  {isClaiming ? "Taking over…" : "Take Over This Request"}
                </Button>
              ))}

            {isReviewer &&
              !["Cancelled", "Rejected", "Closed"].includes(req.status) && (
                <>
                  <Button
                    className="w-full gap-2"
                    variant="outline"
                    onClick={() => setAssignOpen(true)}
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign to…
                  </Button>
                  <AssignDialog
                    request={req}
                    open={assignOpen}
                    onOpenChange={setAssignOpen}
                  />
                </>
              )}

            {canChangeClassification && (
              <>
                <Button
                  className="w-full gap-2"
                  variant="outline"
                  onClick={() => setClassificationOpen(true)}
                >
                  <Tags className="h-4 w-4" />
                  Change Type or Product Area
                </Button>
                <ChangeClassificationDialog
                  request={req}
                  open={classificationOpen}
                  onOpenChange={setClassificationOpen}
                />
              </>
            )}

            {/* Status change */}
            {!isFinalStatus && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Update Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {statusOptions.length > 0 ? (
                    <>
                      <Select
                        value={newStatus}
                        onValueChange={(v) => {
                          setNewStatus(v as RequestStatus);
                          setRejectReason("");
                          setRejectComment("");
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={`Change from ${STATUS_LABELS[req.status]}...`}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {canApproveFromHere && (
                        <p className="text-xs text-muted-foreground">
                          To approve, use the Approve panel so you can choose
                          the Jira board.
                        </p>
                      )}

                      {isRejecting && (
                        <>
                          <Select
                            value={rejectReason}
                            onValueChange={setRejectReason}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select rejection reason" />
                            </SelectTrigger>
                            <SelectContent>
                              {REJECTION_REASONS.map((r) => (
                                <SelectItem key={r} value={r}>
                                  {r}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            placeholder="Optional comment for requestor…"
                            value={rejectComment}
                            onChange={(e) => setRejectComment(e.target.value)}
                            rows={3}
                            className="resize-y"
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
                      This request cannot be transitioned to a new status. It is
                      in a terminal state.
                    </p>
                  )}

                  {isRejecting ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          className="w-full"
                          disabled={!rejectReason}
                        >
                          Reject Request
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Reject this request?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Reason: <strong>{rejectReason}</strong>. The
                            requestor will be notified by email.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleStatusChange}
                            disabled={reject.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {reject.isPending
                              ? "Rejecting…"
                              : "Confirm Rejection"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleStatusChange}
                      disabled={!newStatus || updateStatus.isPending}
                    >
                      {updateStatus.isPending ? "Updating…" : "Update Status"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Request clarification */}
            {![
              "Approved",
              "Rejected",
              "Completed",
              "Closed",
              "Cancelled",
            ].includes(req.status) &&
              !isFinalStatus && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base text-amber-700">
                      Request Clarification
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Ask the requestor for more details. They will receive an
                      email with a link to respond.
                    </p>
                    <div className="relative">
                      <Textarea
                        ref={clarifyTextareaRef}
                        placeholder="What additional information do you need? (type @ to mention someone)"
                        value={clarifyText}
                        onChange={handleClarifyTextChange}
                        rows={3}
                        className="resize-y"
                      />
                      {showClarifySuggestions &&
                        clarifySuggestions.length > 0 && (
                          <div className="absolute bottom-full left-0 right-0 mb-1 border border-muted-foreground/30 rounded-md bg-white dark:bg-gray-900 shadow-lg z-10">
                            {clarifySuggestions.map((s) => (
                              <button
                                key={s.oid}
                                type="button"
                                onClick={() => handleClarifyMentionSelect(s)}
                                className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-b-0 flex flex-col gap-0.5"
                              >
                                <span className="font-medium">
                                  {s.display_name}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {s.email}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                    {clarifyMentions.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {clarifyMentions.map((m) => (
                          <span
                            key={m.oid}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-950 px-2 py-1 text-xs text-blue-700 dark:text-blue-300"
                          >
                            @{m.name}
                            <button
                              type="button"
                              onClick={() =>
                                setClarifyMentions(
                                  clarifyMentions.filter(
                                    (u) => u.oid !== m.oid,
                                  ),
                                )
                              }
                              className="hover:text-blue-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={handleClarify}
                      disabled={!clarifyText.trim() || clarify.isPending}
                    >
                      <MessageSquare className="h-4 w-4" />
                      {clarify.isPending ? "Sending…" : "Send to Requestor"}
                    </Button>
                  </CardContent>
                </Card>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-sm whitespace-pre-wrap break-words">{value}</p>
    </div>
  );
}
